import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Input, Table, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useCallback, useRef } from "react"

import { budgetFormat } from "../../utils/number/budget-format.ts"
import { validateNumber } from "../../utils/validation/number.ts"
import {
  MAX_PAYMENT_PERCENTAGE_TOTAL,
  paymentPercentageTotal,
  paymentScheduleTotalError,
  validatePaymentScheduleTotal
} from "../../utils/validation/payment-schedule-total.ts"
import InputNumber from "../Input/InputNumber.tsx"
import { DocumentCard } from "./DocumentCard.tsx"
import type {
  DocumentFormData,
  DocumentFormPaymentScheduleItem
} from "./document.types.ts"

const { Text } = Typography

interface DocumentPaymentScheduleSectionProps {
  form: FormInstance<DocumentFormData>
}

export function DocumentPaymentScheduleSection({
  form
}: DocumentPaymentScheduleSectionProps) {
  const key: keyof DocumentFormData = "payment_schedule"

  const isUpdatingRef = useRef(false)

  const paymentSchedule = Form.useWatch("payment_schedule", form) || []
  const projectTotal = Form.useWatch(["cost_summary", "total"], form) || 0

  const totalAmount = paymentSchedule.reduce(
    (sum: number, item) => sum + (+item?.amount || 0),
    0
  )

  const totalPercentage = paymentPercentageTotal(paymentSchedule)

  const handleAmountChange = useCallback(
    (fieldIndex: number, newAmount: number | null) => {
      if (isUpdatingRef.current || !projectTotal) return

      isUpdatingRef.current = true

      const percentage =
        newAmount && projectTotal
          ? Math.round((newAmount / projectTotal) * 100 * 100) / 100
          : 0

      const currentSchedule = form.getFieldValue(key) || []
      const updated = [...currentSchedule]
      updated[fieldIndex] = {
        ...updated[fieldIndex],
        amount: newAmount,
        percentage
      }

      form.setFieldsValue({ [key]: updated })

      setTimeout(() => {
        isUpdatingRef.current = false
      }, 0)
    },
    [form, key, projectTotal]
  )

  const handlePercentageChange = useCallback(
    (fieldIndex: number, newPercentage: number | null) => {
      if (isUpdatingRef.current || !projectTotal) return

      isUpdatingRef.current = true

      const amount =
        newPercentage && projectTotal
          ? Math.round((newPercentage / 100) * projectTotal * 100) / 100
          : 0

      const currentSchedule = form.getFieldValue(key) || []
      const updated = [...currentSchedule]
      updated[fieldIndex] = {
        ...updated[fieldIndex],
        percentage: newPercentage,
        amount
      }

      form.setFieldsValue({ [key]: updated })

      setTimeout(() => {
        isUpdatingRef.current = false
      }, 0)
    },
    [form, key, projectTotal]
  )

  const isPercentageExceeded = totalPercentage > 100

  return (
    <DocumentCard title="Payment Schedule" name={key}>
      <Form.List
        name={key}
        rules={[{ validator: validatePaymentScheduleTotal }]}
      >
        {(fields, { add, remove }) => (
          <>
            <Table
              pagination={false}
              dataSource={fields}
              scroll={{ x: "max-content" }}
              rowKey="key"
              bordered
              columns={[
                {
                  title: "Stage",
                  width: "20%",
                  dataIndex: "name",
                  key: "milestone",
                  render: (_, field) => (
                    <Form.Item<DocumentFormPaymentScheduleItem[]>
                      name={[field.name, "milestone"]}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: "Required"
                        }
                      ]}
                    >
                      <Input placeholder="Enter text" />
                    </Form.Item>
                  )
                },
                {
                  title: "Description",
                  dataIndex: "name",
                  key: "description",
                  render: (_, field) => (
                    <Form.Item<DocumentFormPaymentScheduleItem[]>
                      name={[field.name, "description"]}
                      style={{ margin: 0 }}
                    >
                      <Input placeholder="Enter description" />
                    </Form.Item>
                  )
                },
                {
                  title: "Amount",
                  width: 200,
                  dataIndex: "name",
                  key: "amount",
                  align: "right",
                  render: (_, field) => (
                    <Form.Item<DocumentFormPaymentScheduleItem[]>
                      name={[field.name, "amount"]}
                      style={{ margin: 0 }}
                      rules={[{ validator: validateNumber }]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="0"
                        suffix={"$"}
                        min={0}
                        max={99999999.99}
                        status={isPercentageExceeded ? "warning" : undefined}
                        precision={2}
                        onChange={(value) =>
                          handleAmountChange(field.name, value)
                        }
                      />
                    </Form.Item>
                  )
                },
                {
                  title: "%",
                  width: 130,
                  dataIndex: "name",
                  key: "percentage",
                  align: "right",
                  render: (_, field) => (
                    <Form.Item<DocumentFormPaymentScheduleItem[]>
                      name={[field.name, "percentage"]}
                      style={{ margin: 0 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        status={isPercentageExceeded ? "warning" : undefined}
                        max={MAX_PAYMENT_PERCENTAGE_TOTAL}
                        placeholder="0"
                        suffix={"%"}
                        precision={2}
                        onChange={(value) =>
                          handlePercentageChange(field.name, value)
                        }
                      />
                    </Form.Item>
                  )
                },
                {
                  title: "",
                  width: 50,
                  render: (_, field) => (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  )
                }
              ]}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <strong>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Flex vertical>
                        <Text
                          strong
                          type={isPercentageExceeded ? "warning" : undefined}
                        >
                          {budgetFormat(totalAmount)}
                        </Text>
                        <Text type={"secondary"}>
                          of {budgetFormat(projectTotal)}
                        </Text>
                      </Flex>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text
                        strong
                        type={isPercentageExceeded ? "warning" : undefined}
                      >
                        {totalPercentage?.toFixed(2)}%
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
              footer={() => (
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Payment Stage
                </Button>
              )}
            />
            <Form.ErrorList
              errors={
                isPercentageExceeded
                  ? [paymentScheduleTotalError(totalPercentage)]
                  : []
              }
            />
          </>
        )}
      </Form.List>
    </DocumentCard>
  )
}
